import React, { useEffect } from "react";
import { FieldErrors, UseFormRegister } from "react-hook-form";
import PhoneInput from "../../components/PhoneInput";
import { REGEXES } from "../../lib/constants";
import { ImInfo } from "react-icons/im";
import InfoTooltip from "../../components/common/InfoTooltip";

type AccountFormInputs = {
  firstName: string;
  lastName: string;
  phone: number;
  email: string;
};

type AccountInformationProps = {
  setValue: any;
  register: UseFormRegister<AccountFormInputs>;
  errors: FieldErrors<AccountFormInputs>;
  clearErrors: any;
  setSelectedCode: (arg: string) => void;
  selectedCode: string;
  phoneNumber: string;
  onPhoneChange: (code: string, number: string) => void;
  setPhoneNumber: any;
  watch: any;
  setShowAlertOnRouteChange: React.Dispatch<React.SetStateAction<boolean>>;
};

const AccountManagement: React.FC<AccountInformationProps> = ({
  setValue,
  register,
  errors,
  setSelectedCode,
  selectedCode,
  phoneNumber,
  onPhoneChange,
  clearErrors,
  setPhoneNumber,
  watch,
  setShowAlertOnRouteChange,
}) => {
  const handleCodeChange = (newCode: string) => {
    setShowAlertOnRouteChange(true);
    setSelectedCode(newCode);
    setValue("countryCode", selectedCode);
    onPhoneChange(newCode, phoneNumber);
  };

  const handlePhoneInputChange = (phone: string) => {
    setShowAlertOnRouteChange(true);
    const value = phone === " " ? undefined : phone;
    setValue("phone", value);
    onPhoneChange(selectedCode, value as string);
  };

  useEffect(() => {
    setPhoneNumber(watch("phone"));
    setSelectedCode(watch("countryCode"));
  }, []);

  return (
    <div className="w-full grid pb-2">
      <div className="col-span-4 space-y-6 w-full flex flex-col">
        <div className="bg-white p-4 shadow-sm rounded-lg lg:grid gap-x-3 grid-cols-2 flex flex-col">
          <h1 className="lg:col-span-2 font-helveticaMedium mb-2 text-lg flex items-center">
            Contact Information
            <InfoTooltip
              placement="left"
              content="This information will be used for Stripe payment processing and account verification."
            />
          </h1>

          <div className="lg:col-span-1 flex flex-col">
            <label className="text-sm font-helveticaMedium text-customBlackColor">
              First Name <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              {...register("firstName", { required: true })}
              className="focus:outline-customGreenColor w-full mt-1 px-3 py-2 text-sm bg-grayBackgroundColor border border-gray-300 rounded-md shadow-sm"
              placeholder="Type first name here"
            />
            {errors.firstName && (
              <span className="text-red-600 text-xs">
                {errors.firstName.message}
              </span>
            )}
          </div>

          <div className="lg:col-span-1 flex flex-col">
            <label className="text-sm font-helveticaMedium text-customBlackColor">
              Last Name <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              {...register("lastName", { required: true })}
              className="focus:outline-customGreenColor w-full mt-1 px-3 py-2 text-sm bg-grayBackgroundColor border border-gray-300 rounded-md shadow-sm"
              placeholder="Type last name here"
            />
            {errors.lastName && (
              <span className="text-red-600 text-xs">
                {errors.lastName.message}
              </span>
            )}
          </div>

          <div className="lg:col-span-2 mt-3 flex flex-col">
            <label className="text-sm font-helveticaMedium text-customBlackColor flex items-center">
              Phone Number <span className="text-red-600">*</span>
              <InfoTooltip content="This number will be used for delivery updates, order notifications, and communications with riders or delivery personnel. Please ensure it's a number you can access during business hours." />
            </label>
            <PhoneInput
              selectedCode={selectedCode}
              error={errors?.phone ? true : false}
              selectedNumber={phoneNumber}
              setSelectedNumber={handlePhoneInputChange}
              setSelectedCode={handleCodeChange}
              {...register("phone", {
                validate: (value) => {
                  if (value && value.toString().trim() !== "") {
                    return REGEXES.PHONE.test(value)
                      ? true
                      : "Phone number must be exactly 10 digits";
                  }
                  return true;
                },
              })}
            />
            {errors?.phone && (
              <p className="text-red-600 text-sm">{errors?.phone.message}</p>
            )}
          </div>

          <div className="lg:col-span-2 mt-3 flex flex-col">
            <label className="text-sm font-helveticaMedium text-customBlackColor flex items-center">
              Email <span className="text-red-600">*</span>
              <InfoTooltip content="Your primary email address for account communications. This email cannot be changed as it's linked to your account security." />
            </label>
            <input
              type="text"
              {...register("email")}
              className="focus:outline-customGreenColor w-full mt-1 px-3 py-2 text-sm bg-grayBackgroundColor border border-gray-300 rounded-md shadow-sm"
              placeholder="Type email address here"
              disabled
              readOnly
            />
          </div>
        </div>

        {/* <div className='bg-white p-4 shadow-sm rounded-lg gap-3 gap-y-8 pb-8 flex flex-col'>
                    <div>
                        <h1 className='font-helveticaMedium mb-2 text-lg'>Notifications</h1>
                        <h2 className='font-helvetica text-sm text-customGrayBase'>Manage how and when you receive notifications</h2>
                    </div>
                    <div className='grid grid-cols-3'>
                        <h1 className='col-span-2 text-customGrayBase'>Email</h1>
                        <Switch className='col-span-1' />
                    </div>

                    <div className='grid grid-cols-3'>
                        <h1 className='col-span-2 text-customGrayBase'>SMS</h1>
                        <Switch className='col-span-1' />
                    </div>

                    <div className='grid grid-cols-3'>
                        <h1 className='col-span-2 text-customGrayBase'>App</h1>
                        <Switch className='col-span-1' />
                    </div>
                </div> */}
      </div>
    </div>
  );
};
export default AccountManagement;
